/**
 * Standing contracts (`Blanket Order`) and what they mean for an enquiry's lines.
 *
 * ---- The domain ----
 *
 * Provision items — milk, bread, fruit — are bought on standing contracts with a named supplier
 * at a rate already agreed. A line for such an item must NOT go out to RFQ: the price is known,
 * and asking suppliers to quote something already contracted wastes days. The purchaser selects
 * which contracts are in play (several suppliers may cover the same enquiry), and these functions
 * decide what that selection means for the table.
 *
 * ---- Measured facts this is built on (2026-08-29, rfqdev) ----
 *
 * - 17 blanket orders, ALL `status: "Active"`, all `is_expired: false`, 425 distinct item codes.
 * - Six suppliers hold TWO OR THREE orders each with heavily overlapping items (Fruits Garden:
 *   180/177/177). One item carrying several rates is therefore the normal case, not an edge one.
 * - Every item row comes back `"uom": null`, so a rate's own unit is unknown. It is carried
 *   through as `null` and shown against the LINE's unit rather than guessed at.
 * - `Enquiry Item` has no field for a blanket order's NAME: an unknown key on `update_enquiry`
 *   is silently dropped (measured). `route`, `selected_rate` and `suppliers` DO persist, which is
 *   what `selectedOrdersForLines` reconstructs the selection from after a reload.
 */

import { money } from './money'
import { IN_HOUSE_SUPPLIER } from './directPricing'
import type { EnquiryItem, ItemContract } from './types'

/** One order's item list, as `items.get_blanket_order_item` returns it. */
export interface BlanketOrderDetail {
  name: string
  supplier: string
  currency?: string | null
  items: Array<{ item_code: string; item_name?: string; rate: number; uom?: string | null; qty?: number }>
}

/** One contracted rate for one item, from one order. */
export interface BlanketRate {
  order: string
  supplier: string
  rate: number
  currency: string
  /** Always `null` on this bench — the wire does not carry the rate's unit. */
  uom: string | null
}

/**
 * `item_code → every contracted rate for it`, cheapest first.
 *
 * Every rate is kept rather than reduced to one: with the same item on two or three of a
 * supplier's orders, silently taking one would hide a cheaper contract, and the purchaser is the
 * one who decides. Cheapest leads so the default applied to a line is the best available price.
 *
 * A rate of zero (or missing) is DROPPED. It is an unfilled row, not a price — and routing a line
 * to contract on it would both quote the customer nothing and skip the RFQ that would have found
 * a real number.
 */
export function buildRateIndex(orders: BlanketOrderDetail[]): Map<string, BlanketRate[]> {
  const index = new Map<string, BlanketRate[]>()
  for (const order of orders) {
    for (const row of order.items ?? []) {
      const rate = Number(row.rate)
      if (!Number.isFinite(rate) || rate <= 0) continue
      const code = String(row.item_code ?? '').trim()
      if (code === '') continue
      const list = index.get(code) ?? []
      list.push({
        order: order.name,
        supplier: order.supplier,
        rate,
        currency: order.currency || 'AED',
        uom: row.uom ?? null,
      })
      index.set(code, list)
    }
  }
  for (const list of index.values()) list.sort((a, b) => a.rate - b.rate)
  return index
}

export interface ContractLineAction {
  lineId: string
  itemCode: string
  rate: number
  supplier: string
  order: string
  /** How many contracted rates exist for this item — >1 means the row offers a choice. */
  alternatives: number
}

/**
 * Which lines the selection turns into contract lines, and at what rate.
 *
 * Applied automatically: a line whose item is on a selected contract is contract business by
 * definition, and making the purchaser confirm that per line on a sixty-line provision enquiry is
 * the work this feature exists to remove. The rate applied is the cheapest; `alternatives` tells
 * the row to offer the others, which the existing per-line contract dropdown already renders.
 *
 * A line already saying exactly this — same route, same rate, same supplier — is skipped, so
 * re-selecting the same orders is a no-op rather than a pointless write.
 */
export function planContractLines(
  lines: EnquiryItem[],
  index: ReadonlyMap<string, BlanketRate[]>,
): ContractLineAction[] {
  const plan: ContractLineAction[] = []
  for (const line of lines) {
    if (!line.item_code) continue
    const rates = index.get(line.item_code)
    if (!rates || rates.length === 0) continue
    const best = rates[0]
    /*
      Already priced from ANY of the selected contracts — not merely the cheapest.

      This used to compare against `best` alone, so a purchaser who deliberately picked the dearer
      of two contracts had their line planned again on the very next render and overwritten with
      the cheapest. The choice reverted itself, and the table blinked while it happened.

      A line sitting on a rate and supplier that a SELECTED contract offers is a decision, not a
      gap. A rate belonging to no selected contract still re-plans — that one is stale, because
      the order it came from has been unticked.
    */
    const rate = Number((line as { selected_rate?: number }).selected_rate)
    const already =
      line.route === 'contract' &&
      line.suppliers.length === 1 &&
      rates.some((r) => r.rate === rate && r.supplier === line.suppliers[0])
    if (already) continue
    plan.push({
      lineId: line.line_id,
      itemCode: line.item_code,
      rate: best.rate,
      supplier: best.supplier,
      order: best.order,
      alternatives: rates.length,
    })
  }
  return plan
}

/** What `revertContractLines` writes per line — the inverse of `ContractLineAction`'s patch. */
export interface ContractLineRevert {
  lineId: string
  patch: { route: 'rfq'; selected_rate: 0; suppliers: []; contract_id: null }
}

/**
 * The lines to take BACK when orders are unticked (Akhil, 2026-09-16: after Clear "the item
 * contract route still stays there").
 *
 * Unticking used to be cosmetic: the lines kept their route, rate and contract supplier, so the
 * enquiry still read as contract business — and `selectedOrdersForLines` re-ticked the same
 * orders from those very lines on the next load. The clear did not survive a reload.
 *
 * A line is reverted only when a REMOVED order is what priced it: contract-routed, on exactly
 * that order's supplier, at a rate that order carries for the item (`index` is the index of the
 * orders as they were BEFORE the change, so the removed rates are still in it). A rate a kept
 * order also offers stays — the purchaser still has a contract for it. Everything else is left
 * alone: an in-house line (`IN_HOUSE_SUPPLIER`, never an order's supplier), a line priced by
 * hand at a figure on no order, an RFQ line.
 */
export function revertContractLines(
  lines: EnquiryItem[],
  index: ReadonlyMap<string, BlanketRate[]>,
  removedOrders: readonly string[],
): ContractLineRevert[] {
  const removed = new Set(removedOrders)
  const out: ContractLineRevert[] = []
  for (const line of lines) {
    if (line.route !== 'contract' || !line.item_code || line.suppliers.length !== 1) continue
    const rates = index.get(line.item_code) ?? []
    const rate = Number(line.selected_rate)
    const supplier = line.suppliers[0]
    const mine = rates.filter((r) => r.rate === rate && r.supplier === supplier)
    if (mine.length === 0) continue
    if (mine.some((r) => !removed.has(r.order))) continue
    out.push({ lineId: line.line_id, patch: { route: 'rfq', selected_rate: 0, suppliers: [], contract_id: null } })
  }
  return out
}

/**
 * What a change of the row's route dropdown writes (Akhil, 2026-09-16).
 *
 * "Send RFQ" used to write `route` alone, so the line arrived on the RFQ side still carrying the
 * contract's rate and — worse — the contract's supplier as the supplier to ask: "the contract
 * became a supplier in that line item". Moving to RFQ means the price is dropped, and with it
 * whoever it came from, when that was a blanket order (`index` — every active order, not only the
 * ticked ones) or our own shelf. A supplier whose PAST QUOTE priced the line is kept: they are a
 * sensible recipient of the RFQ that replaces it.
 *
 * "Provision · contract" writes only the route; the price arrives from the contract picked next
 * (the row's contract list, or the bar's plan).
 */
export function routeChangePatch(
  line: EnquiryItem,
  route: 'contract' | 'rfq',
  index: ReadonlyMap<string, BlanketRate[]>,
): { route: 'contract' | 'rfq'; selected_rate?: 0; suppliers?: [] } {
  if (route === 'contract') return { route }
  const supplier = line.suppliers.length === 1 ? line.suppliers[0] : null
  const rate = Number(line.selected_rate)
  const fromContract =
    supplier !== null &&
    !!line.item_code &&
    (index.get(line.item_code) ?? []).some((r) => r.supplier === supplier && r.rate === rate)
  const fromShelf = supplier === IN_HOUSE_SUPPLIER
  return fromContract || fromShelf ? { route, selected_rate: 0, suppliers: [] } : { route, selected_rate: 0 }
}

/**
 * Which orders to re-tick when the enquiry is opened again.
 *
 * The selection itself cannot be persisted — `Enquiry Item` has nowhere to put a blanket order's
 * name (see the header). What survives is each contract line's SUPPLIER, so the selection is
 * rebuilt from the suppliers actually priced on the enquiry: every active order belonging to one
 * of them comes back ticked.
 *
 * Supplier-level, not order-level, and deliberately so — reconstructing which of a supplier's
 * three orders was chosen is not possible from what is stored, and guessing would silently show
 * the wrong contract. Filed as an ask for a `blanket_order` link on the line.
 */
export function selectedOrdersForLines(
  orders: Array<{ name: string; supplier: string }>,
  lines: EnquiryItem[],
): string[] {
  const suppliers = new Set<string>()
  for (const line of lines) {
    if (line.route !== 'contract') continue
    for (const s of line.suppliers) suppliers.add(s)
  }
  if (suppliers.size === 0) return []
  return orders.filter((o) => suppliers.has(o.supplier)).map((o) => o.name)
}


/**
 * The selected contracts that cover one item, in the shape the row already renders.
 *
 * `LineRow` has shown `item.contracts` as a per-line dropdown of rate + supplier since before
 * this feature existed, and shows it exactly when `route === 'contract'`. Converting into that
 * shape means "pick the other contract" needs no new control: the two or three rates a duplicated
 * item carries appear where a purchaser already looks for them.
 *
 * `valid_till` is null throughout — no blanket order on this bench carries validity dates, and an
 * invented one would claim an expiry nobody set.
 */
export function contractsForItem(
  index: ReadonlyMap<string, BlanketRate[]>,
  itemCode: string | null | undefined,
): ItemContract[] {
  if (!itemCode) return []
  return (index.get(itemCode) ?? []).map((r) => ({
    id: r.order,
    supplier: r.supplier,
    rate: money(r.rate, r.currency),
    valid_till: null,
  }))
}


/**
 * Which of an item's contracts a line is actually priced from.
 *
 * ---- Why this cannot just read `contract_id` ----
 *
 * `contract_id` does not survive a write. `Enquiry Item` has no field for a blanket order, and
 * `applyItemPatch` drops the key deliberately rather than sending something the server will
 * ignore. What DOES persist is `selected_rate` and `suppliers` — the rate the customer will be
 * quoted and who it came from — so those identify the contract after a reload.
 *
 * Matching on rate ALONE is not enough: two suppliers commonly hold the same rate for the same
 * item, and showing the wrong supplier's contract beside a price is how the wrong company gets
 * the order.
 *
 * `contract_id` still wins while it exists, because a purchaser who has just picked from the
 * dropdown should see their choice immediately, before the write lands.
 *
 * Falls back to the CHEAPEST rather than to nothing — that is what `planContractLines` applied in
 * the first place, so it is the honest default for a line nothing else identifies.
 */
export function chosenContractFor(
  contracts: ItemContract[],
  line: Pick<EnquiryItem, 'contract_id' | 'suppliers'> & { selected_rate?: number },
): ItemContract | undefined {
  if (contracts.length === 0) return undefined
  const byId = line.contract_id ? contracts.find((c) => c.id === line.contract_id) : undefined
  if (byId) return byId

  const rate = line.selected_rate
  const supplier = line.suppliers.length === 1 ? line.suppliers[0] : null
  if (rate !== undefined && rate > 0) {
    const exact = contracts.find((c) => c.rate.amount === rate && (supplier === null || c.supplier === supplier))
    if (exact) return exact
  }
  // Cheapest first, as `buildRateIndex` ordered them.
  return contracts[0]
}

/**
 * Whether a line's contracts must be read from the server rather than from the selected-orders
 * index — see the test's own note. The index is empty for the first seconds after the RFQ page
 * loads, and an item change in that window used to price the line as if the item had no contract.
 *
 * `touched` is the purchaser having used the contract bar: an empty selection is then a decision,
 * not a gap, and nothing should look behind it.
 */
export function needsContractLookup(input: { provision: boolean; found: number; touched: boolean; ready: boolean }): boolean {
  return input.provision && input.found === 0 && !input.touched && !input.ready
}
